import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import type { AppTitleLogResponse } from '@/types/reports';

export interface UseAppTitleLogArgs {
  /** Period query string in the same shape ReportsMonthlyPage builds — e.g.
   *  `month=2026-04` or `period=week&week=2026-W17`. Pass `null` to disable. */
  periodQs: string | null;
  clientId?: string;
  projectId?: string;
  employeeId?: string;
  limit?: number;
}

interface UseAppTitleLogResult {
  data: AppTitleLogResponse | null;
  isLoading: boolean;
}

const EMPTY_REPORT: AppTitleLogResponse = {
  org: { id: '', name: '' },
  startDate: '',
  endDate: '',
  filters: {},
  rows: [],
  totalGroups: 0,
  summary: { totalEvents: 0, totalSessions: 0, totalHours: 0 },
};

/**
 * App & Window Log report data source — task 169.
 *
 * Fires the JSON endpoint with the encoded period (reusing the monthly
 * report's URL convention) and the three optional narrowing filters.
 * In mockup mode returns an empty report — the page handles the
 * empty-state rendering uniformly.
 */
export function useAppTitleLog(args: UseAppTitleLogArgs): UseAppTitleLogResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;
  const enabled = !mockupMode && !!orgId && !!args.periodQs;

  const query = useQuery({
    queryKey: ['appTitleLog', orgId, args],
    queryFn: async () => {
      const token = await getToken();
      const params = new URLSearchParams(args.periodQs ?? '');
      if (args.clientId) params.set('clientId', args.clientId);
      if (args.projectId) params.set('projectId', args.projectId);
      if (args.employeeId) params.set('employeeId', args.employeeId);
      if (args.limit !== undefined) params.set('limit', String(args.limit));
      return apiFetch<AppTitleLogResponse>(
        `/api/v1/orgs/${orgId}/reports/activity-log?${params.toString()}`,
        token,
      );
    },
    enabled,
  });

  if (mockupMode) return { data: EMPTY_REPORT, isLoading: false };
  return { data: query.data ?? null, isLoading: query.isLoading };
}
